// Header2: A modern, sleek header with a white background and right-aligned navigation.
// It features smooth animations for scroll and hover states, and a clean dropdown menu.

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '../ui/button'
import { ChevronDown, ChevronUp, Menu, X } from 'lucide-react'
import logo from '../../assets/logo.png'
import { Link, useLocation, useNavigate } from 'react-router'

// Defines the structure of a navigation item, including potential children for a dropdown.
interface HeaderProps {
  navItems: {
    path: string
    label: string
    children?: { path: string; label: string }[]
  }[]
  isDemo?: boolean
}

export function Header2({ navItems, isDemo }: HeaderProps) {
  // Accesses the current location and navigation functions from react-router.
  const location = useLocation()
  const navigate = useNavigate()

  // State for controlling mobile menu visibility, dropdown active state, and header visibility on scroll.
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeParent, setActiveParent] = useState<string | null>(null)
  const [showHeader, setShowHeader] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)

  // A side effect hook that listens for scroll events to hide/show the header.
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Show header on scroll up or at the very top of the page. Hide on scroll down.
      if (currentScrollY < lastScrollY || currentScrollY <= 50) {
        setShowHeader(true)
      } else {
        setShowHeader(false)
      }

      setLastScrollY(currentScrollY)
    }

    // Attaches and cleans up the scroll event listener.
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY]) // Dependency array ensures effect runs only when lastScrollY changes.

  // Handles navigation to a new page and closes the mobile menu.
  const scrollToSection = (path: string) => {
    navigate(path)
    setIsMenuOpen(false)
    window.scrollTo(0, 0)
  }

  // Sets the active parent for dropdown menu on mouse enter.
  const handleMouseEnter = (parent: string) => {
    setActiveParent(parent)
  }

  // Clears the active parent on mouse leave.
  const handleMouseLeave = () => {
    setActiveParent(null)
  }

  return (
    // The main header element with motion for smooth transitions.
    <motion.header
      className={`top-0 left-0 w-full transition-all duration-300 z-50 ${!isDemo ? 'fixed' : ''} ${
        showHeader ? 'translate-y-0 opacity-100 ' : ''
      } bg-white backdrop-blur-md shadow-lg border-b border-gray-200`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ">
        <div className="flex justify-between items-center h-20">
          {/* 🖼️ Logo */}
          <Link to={'/'}>
            <motion.div
              className="flex items-center space-x-3"
              whileHover={{ scale: 1.05 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <img className="h-10" src={logo} alt="logo" />
            </motion.div>
          </Link>

          <div className="flex items-center space-x-8">
            {/* 🧭 Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navItems.map((item, index) => {
                const isActive =
                  location?.pathname === item.path ||
                  item.children?.some(
                    (child) => location.pathname === child.path
                  )

                return (
                  <div
                    key={item.label}
                    className="relative group"
                    onMouseEnter={() => handleMouseEnter(item.label)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {item.children ? (
                      <>
                        {/* Parent Link with Dropdown */}
                        <motion.div
                          className={`font-medium cursor-pointer relative transition-colors flex items-center ${
                            isActive
                              ? 'text-green-600 font-semibold'
                              : 'text-gray-700 hover:text-green-600'
                          }`}
                          initial={{ opacity: 0, y: -20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          {item.label}
                          {activeParent === item.label || isActive ? (
                            <ChevronUp className="w-4 h-4 ml-1" />
                          ) : (
                            <ChevronDown className="w-4 h-4 ml-1" />
                          )}
                        </motion.div>

                        {/* Dropdown Items */}
                        <div
                          className={`absolute hidden group-hover:block top-full pt-5 z-50 right-0 ${
                            activeParent === item.label ? 'block' : 'hidden'
                          }`}
                        >
                          <div className="w-48 bg-white rounded-md shadow-lg py-2 z-10 transition-all duration-300 origin-top scale-y-0 group-hover:scale-y-100 border-[1px] border-gray-200 overflow-hidden">
                            {item.children.map((child) => (
                              <motion.button
                                key={child.label}
                                onClick={() => scrollToSection(child.path)}
                                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-green-600 transition-colors"
                                whileHover={{ x: 5 }}
                                transition={{ type: 'spring', stiffness: 500 }}
                              >
                                {child.label}
                              </motion.button>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      // Single link
                      <motion.button
                        onClick={() => scrollToSection(item.path)}
                        className={`relative font-medium transition-colors ${
                          isActive
                            ? 'text-green-600 font-semibold'
                            : 'text-gray-700 hover:text-green-600'
                        }`}
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        {item.label}
                      </motion.button>
                    )}
                  </div>
                )
              })}
            </nav>

            {/* 📱 Mobile Menu / CTA */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Button
                onClick={() => {
                  navigate('/contact')
                }}
                className="hidden md:flex bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-full"
              >
                Get Free Quote
              </Button>
            </motion.div>

            {/* 🍔 Mobile Menu Toggle */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              {isMenuOpen ? (
                <X className="w-5 h-5 text-gray-700" />
              ) : (
                <Menu className="w-5 h-5 text-gray-700" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* 📱 Mobile Nav */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden border-t border-gray-200 bg-white"
          >
            <div className="px-4 py-6 space-y-4">
              {navItems.map((item, index) => (
                <div key={item.label}>
                  <motion.button
                    onClick={() =>
                      item.children ? null : scrollToSection(item.path)
                    }
                    className={`block w-full text-left font-medium py-2 transition-colors ${
                      location.pathname === item.path
                        ? 'text-green-600 font-semibold'
                        : 'text-gray-700 hover:text-green-600'
                    }`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {item.label}
                  </motion.button>
                  {item.children && (
                    <motion.div
                      className="pl-4 border-l border-gray-200 mt-2 space-y-2"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      {item.children.map((child) => (
                        <motion.button
                          key={child.label}
                          onClick={() => scrollToSection(child.path)}
                          className={`block w-full text-left text-sm py-1 transition-colors ${
                            location.pathname === child.path
                              ? 'text-green-600 font-semibold'
                              : 'text-gray-600 hover:text-green-600'
                          }`}
                          initial={{ x: -10, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: 0.2 }}
                        >
                          {child.label}
                        </motion.button>
                      ))}
                    </motion.div>
                  )}
                </div>
              ))}
              <Button
                onClick={() => {
                  navigate('/contact')
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white mt-4 rounded-full"
              >
                Get Free Quote
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  )
}
